import { Outlet, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'
import { AlertTriangle, LogOut, Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'

// Check if running in development/local environment by hostname
const isDev = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.startsWith('10.') ||
  window.location.port === '3025' ||
  window.location.port === '3026'
)

// Control-room nav: grouped, numbered, collapsible
interface NavItem {
  name: string
  href: string
}
interface NavGroup {
  label: string // '' = ungrouped (Dashboard)
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  { label: '', items: [{ name: 'Dashboard', href: '/' }] },
  {
    label: 'CONTENT',
    items: [
      { name: 'Streams', href: '/streams' },
      { name: 'Categories', href: '/categories' },
      { name: 'Bouquets', href: '/bouquets' },
      { name: 'Series', href: '/series' },
    ],
  },
  {
    label: 'SOURCES',
    items: [
      { name: 'Pluto TV', href: '/plutotv' },
      { name: 'Tubi', href: '/tubi' },
      { name: 'Live Events', href: '/live-events' },
      { name: 'Import', href: '/import' },
    ],
  },
  {
    label: 'GUIDE',
    items: [
      { name: 'EPG', href: '/epg' },
      { name: 'Sports EPG', href: '/sports-epg' },
    ],
  },
  {
    label: 'ACCESS',
    items: [
      { name: 'Users', href: '/users' },
      { name: 'Analytics', href: '/analytics' },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { name: 'Transcoding', href: '/transcoding' },
      { name: 'Settings', href: '/settings' },
    ],
  },
]

// Sequential 01..NN numbers across all groups
const NAV_NUMBERS = new Map<string, string>()
{
  let n = 0
  for (const g of NAV_GROUPS) {
    for (const it of g.items) {
      n++
      NAV_NUMBERS.set(it.href, String(n).padStart(2, '0'))
    }
  }
}

const PAGE_META: Record<string, [string, string]> = {
  '/': ['Dashboard', 'OVERVIEW'],
  '/streams': ['Streams', 'CONTENT'],
  '/categories': ['Categories', 'CONTENT'],
  '/bouquets': ['Bouquets', 'CONTENT'],
  '/series': ['Series', 'CONTENT'],
  '/plutotv': ['Pluto TV', 'SOURCES'],
  '/tubi': ['Tubi', 'SOURCES'],
  '/live-events': ['Live Events', 'SOURCES'],
  '/import': ['Import', 'SOURCES'],
  '/epg': ['EPG', 'GUIDE'],
  '/sports-epg': ['Sports EPG', 'GUIDE'],
  '/users': ['Users', 'ACCESS'],
  '/analytics': ['Analytics', 'ACCESS'],
  '/transcoding': ['Transcoding', 'SYSTEM'],
  '/settings': ['Settings', 'SYSTEM'],
}

export default function Layout() {
  const { logout, user } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  // Panel v2 is dark-only (control room); clear any stale light preference
  useEffect(() => {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  }, [])

  // Header status pill — real reachability, not decoration
  const { isError: apiDown } = useQuery({
    queryKey: ['panel-heartbeat'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 30000,
    retry: 1,
  })

  // In dev mode, change the favicon to red and update title
  useEffect(() => {
    if (isDev) {
      // Update title
      document.title = '🔴 DEV - OBA Admin Panel'

      // Create a red favicon dynamically
      const canvas = document.createElement('canvas')
      canvas.width = 32
      canvas.height = 32
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // Red circle background
        ctx.fillStyle = '#dc2626'
        ctx.beginPath()
        ctx.arc(16, 16, 15, 0, Math.PI * 2)
        ctx.fill()

        // White "D" for Dev
        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('D', 16, 17)

        // Set as favicon
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link')
        link.type = 'image/png'
        link.rel = 'icon'
        link.href = canvas.toDataURL()
        document.head.appendChild(link)
      }
    }
  }, [])

  const [pageTitle, pageCrumb] = PAGE_META[location.pathname] ?? ['OBA', 'PANEL']

  return (
    <div className="min-h-screen bg-background">
      {/* Dev environment banner */}
      {isDev && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white py-1 px-4 flex items-center justify-center gap-2 text-sm font-bold shadow-lg">
          <AlertTriangle className="w-4 h-4" />
          <span>DEVELOPMENT ENVIRONMENT</span>
          <AlertTriangle className="w-4 h-4" />
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/55 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[232px] bg-sidebar transform transition-transform duration-200 lg:translate-x-0',
          'flex flex-col border-r border-cr-soft',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          isDev && 'pt-8' // Account for dev banner
        )}
      >
        {/* Logo section */}
        <div className="flex items-center gap-2.5 px-[18px] pt-[18px] pb-3.5 border-b border-cr-soft relative">
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <img src="/logo.png" alt="OBA" className="h-9 w-auto" />
            <span className="flex flex-col gap-0.5">
              <span className="font-bold text-[15px] tracking-wide text-sidebar-foreground">OBA PANEL</span>
              <span className="cr-label text-[10px] tracking-[0.08em]">CONTROL ROOM</span>
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute right-3 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-sidebar-muted lg:hidden transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2.5 py-3 overflow-y-auto">
          {NAV_GROUPS.map((g) => {
            const isCollapsed = !!collapsed[g.label]
            return (
              <div key={g.label || 'root'} className="flex flex-col gap-px mb-1.5">
                {g.label && (
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [g.label]: !c[g.label] }))}
                    className="flex items-center justify-between w-full px-2.5 pt-2.5 pb-1.5 cr-label hover:text-cr-ink1 transition-colors"
                  >
                    <span>{g.label}</span>
                    <span className="text-[9px]">{isCollapsed ? '▸' : '▾'}</span>
                  </button>
                )}
                {!isCollapsed &&
                  g.items.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-2.5 w-full text-left px-2.5 py-[9px] rounded-[7px] text-[13px] font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-cr-ink1 hover:text-foreground hover:bg-sidebar-muted'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span
                          className={cn(
                            'cr-mono text-[10px] w-[18px] shrink-0',
                            isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                          )}
                        >
                          {NAV_NUMBERS.get(item.href)}
                        </span>
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}
              </div>
            )
          })}
        </nav>

        {/* User section */}
        <div className="px-[18px] py-3.5 border-t border-cr-soft flex items-center gap-2.5">
          <div className="flex items-center justify-center w-[30px] h-[30px] rounded-md bg-sidebar-muted border border-border cr-mono text-xs text-primary">
            {user?.username?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-sidebar-foreground truncate">
              {user?.username || 'Admin'}
            </p>
            <p className="cr-label text-[10px] truncate">
              {(user?.role || 'ADMIN').toUpperCase()}
            </p>
          </div>
          <button
            onClick={() => logout()}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('lg:pl-[232px] min-h-screen flex flex-col', isDev && 'pt-8')}>
        {/* Top bar */}
        <header
          className={cn(
            'sticky z-30 flex items-center justify-between gap-2.5 h-14 px-4 sm:px-[18px]',
            'bg-background/90 backdrop-blur-md border-b border-cr-soft',
            isDev ? 'top-8' : 'top-0'
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-1 rounded-[7px] border border-border text-cr-ink1 hover:text-foreground transition-colors lg:hidden"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div className="flex items-baseline gap-3 min-w-0">
              <h1 className="text-base font-bold whitespace-nowrap">{pageTitle}</h1>
              <span className="cr-label hidden sm:inline tracking-[0.08em]">{pageCrumb}</span>
            </div>
          </div>

          {/* Live status pill — reflects real API reachability */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-[7px] px-3 py-[5px] border border-border rounded-full bg-popover">
              <span
                className={cn('cr-dot cr-pulse', apiDown ? 'cr-dot-err' : 'cr-dot-ok')}
                style={{ width: 7, height: 7 }}
              />
              <span className="cr-mono text-[10px] tracking-[0.1em] text-cr-ink1">
                {apiDown ? 'API UNREACHABLE' : (
                  <>
                    <span className="hidden sm:inline">ALL SYSTEMS NOMINAL</span>
                    <span className="sm:hidden">NOMINAL</span>
                  </>
                )}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-3.5 py-4 sm:px-7 sm:py-6 pb-11">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
